/**
 * Simple and direct text to markdown formatter
 * Converts common plain text patterns to proper markdown
 */

export class TextFormatter {
  /**
   * Convert plain text to properly formatted markdown
   */
  static autoFormat(text: string): string {
    if (!text) return text;

    let formatted = text;

    // If the content already contains markdown constructs (fenced code,
    // headings, lists, or explicit markdown links), don't change it.
    // Aggressive auto-formatting has been observed to corrupt markdown
    // produced by the LLM (inserting/removing newlines, changing list
    // markers). We only perform conservative changes for plain text.
    const looksLikeMarkdown = /(^```|^\s*[-*+]\s+|^\s*\d+\.\s+|^#{1,6}\s+|\[.+\]\(.+\))/m.test(formatted);
    if (looksLikeMarkdown) {
      return formatted;
    }

    // 1. Check if we already have proper markdown links
    const hasExistingMarkdownLinks = formatted.includes('[') && formatted.includes('](http');

    // 2. Only process standalone URLs if we don't already have markdown links
    if (!hasExistingMarkdownLinks) {
      formatted = formatted.replace(/(^|[^)])(https?:\/\/[^\s)]+)/g, (_match, prefix, url) => {
        const cleanUrl = url.replace(/[.,;:]$/, '');
        const trailing = url.slice(cleanUrl.length);
        
        let linkText = 'View Document';
        
        // Extract LCD/NCD number from URL if present
        if (cleanUrl.includes('lcd.aspx')) {
          const lcdMatch = cleanUrl.match(/lcdid=(\d+)/i);
          const lcdNum = lcdMatch ? lcdMatch[1] : '';
          linkText = lcdNum ? `View LCD ${lcdNum}` : 'View LCD Document';  
        } else if (cleanUrl.includes('ncd.aspx')) {
          const ncdMatch = cleanUrl.match(/ncdid=(\d+)/i);
          const ncdNum = ncdMatch ? ncdMatch[1] : '';
          linkText = ncdNum ? `View NCD ${ncdNum}` : 'View NCD Document';  
        } else if (cleanUrl.includes('cms.gov')) {
          linkText = 'View CMS Document';
        }
        
        return `${prefix}[${linkText}](${cleanUrl})${trailing}`;
      });
    }    // 3. Fix numbered lists - ensure they're properly formatted
    // 3. Only perform lightweight, non-destructive formatting:
    // - Auto-link bare URLs (done above)
    // - Bold simple well-known tokens like NCD/LCD identifiers without
    //   changing whitespace or inserting newlines which may break markdown
    formatted = formatted.replace(/\b(NCD|LCD)\s+(ID\s*:?\s*[\w\d.,-]+)\b/gi, '**$1 $2**');
    formatted = formatted.replace(/\b(NCD|LCD)\s+([\d.]+)\b/gi, '**$1 $2**');
    formatted = formatted.replace(/\b(FDA|CGM|SNF|I-CGM)\b/g, '**$1**');

    // Trim only leading/trailing whitespace - avoid altering internal newlines
    formatted = formatted.trim();
    
    return formatted;
  }
}

export default TextFormatter;
